import { Ecommerce, EcommerceItem } from './events/ecommerce/types';
import { GAEventName } from './ga-event-name.enum';
import { DataLayerEvent } from './types';

export const ALLOWED_AXON_EVENTS: ReadonlySet<GAEventName> = new Set([
  GAEventName.ADD_SHIPPING_INFO,
  GAEventName.ADD_PAYMENT_INFO,
  GAEventName.ADD_TO_CART,
  GAEventName.PURCHASE,
  GAEventName.HISTORY_CHANGE,
  GAEventName.NEWSLETTER_SIGNUP_SUCCESS,
  GAEventName.LOGIN,
  GAEventName.SIGN_UP,
  GAEventName.VIEW_CONTENT,
  GAEventName.BEGIN_CHECKOUT,
  GAEventName.VIEW_ITEM_LIST
]);

const waitForAxon = (callback: () => void, interval = 100, maxRetries = 50) => {
  let retries = 0;

  const checkAxon = () => {
    if (typeof window.axon === 'function') {
      callback();
    } else if (retries < maxRetries) {
      // eslint-disable-next-line no-plusplus
      retries++;
      setTimeout(checkAxon, interval);
    } else {
      // eslint-disable-next-line no-console
      console.error('Axon is not defined after maximum retries');
    }
  };

  checkAxon();
};

export const logAxonEvent = (event: DataLayerEvent) => {
  const eventName = event.event || event.event_name;
  const eventEcommerce = event.ecommerce as Ecommerce | undefined;

  if (!eventName) return;

  const items = eventEcommerce?.items as EcommerceItem[];
  if (ALLOWED_AXON_EVENTS.has(eventName as GAEventName)) {
    waitForAxon(() => {
      if (eventName === GAEventName.HISTORY_CHANGE) {
        window.axon('track', 'page_view');
      }

      if (eventName === GAEventName.VIEW_CONTENT) {
        const contents = (event.contents as any[])?.map((item: any) => ({
          item_variant_id: item.variant_id,
          item_id: item.id,
          item_name: item.title,
          price: item.price,
          quantity: item.quantity,
          image_url: item.image_url,
          item_category_id: item.category_id
        }));

        window.axon('track', 'view_item', {
          currency: event?.currency,
          value: event?.value,
          items: contents
        });
      }

      if (eventName === GAEventName.ADD_TO_CART) {
        window.axon('track', 'add_to_cart', {
          currency: items[0]?.currency,
          value: items[0]?.price,
          items:
            items &&
            items.map((item: any) => {
              return {
                item_variant: item.variant_id,
                item_name: item.item_name,
                item_id: item.item_id,
                quantity: item.quantity,
                price: item.price
              };
            })
        });
      }

      if (eventName === GAEventName.BEGIN_CHECKOUT) {
        const totalValue = items.reduce((max, item) => {
          const itemTotal =
            item.price && item.quantity ? parseFloat(item.price) * item.quantity : 0;
          return itemTotal > max ? itemTotal : max;
        }, 0);

        window.axon('track', 'begin_checkout', {
          currency: event.currency,
          value: totalValue,
          items
        });
      }

      if (eventName === GAEventName.LOGIN) {
        window.axon('track', 'login', {
          user_id: event.email
        });
      }

      if (eventName === GAEventName.SIGN_UP) {
        window.axon('track', 'sign_up', {
          user_id: event.email,
          method: event.method
        });
      }
    });
  }
};
